import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  Users2, 
  User, 
  Phone, 
  Mail, 
  FileText, 
  Building, 
  Info, 
  AlertCircle 
} from 'lucide-react';

const ReportingInfoModal = ({ candidate, onSave, onClose, mode = 'both' }) => {
  // Set initial active section based on mode
  const [activeSection, setActiveSection] = useState(
    mode === 'reporting' ? 1 : mode === 'project' ? 2 : 1
  );
  
  // Section 1: Reporting Information
  const [workEmail, setWorkEmail] = useState(candidate?.workEmail || '');
  const [reportingManagerName, setReportingManagerName] = useState(candidate?.reportingManagerName || '');
  const [reportingManagerEmail, setReportingManagerEmail] = useState(candidate?.reportingManagerEmail || '');
  const [reportingManagerPhone, setReportingManagerPhone] = useState(candidate?.reportingManagerPhone || '');
  const [reportingComments, setReportingComments] = useState(candidate?.reportingComments || candidate?.reporting_comments || '');
  
  const [errors, setErrors] = useState({});
  const [sectionLoading, setSectionLoading] = useState(false);

  useEffect(() => {
    console.log('🔍 Modal opened with candidate data:', {
      id: candidate?.id,
      name: candidate?.candidateName,
      reportingComments: candidate?.reportingComments,
      reporting_comments: candidate?.reporting_comments,
    });
  }, [candidate]);

  useEffect(() => {
    if (candidate) {
      setWorkEmail(candidate.workEmail || '');
      setReportingManagerName(candidate.reportingManagerName || '');
      setReportingManagerEmail(candidate.reportingManagerEmail || '');
      setReportingManagerPhone(candidate.reportingManagerPhone || '');
      setReportingComments(candidate.reportingComments || candidate.reporting_comments || '');
    }
  }, [candidate]);
  
  const validateSection1 = () => {
    const newErrors = {};
    
    if (workEmail && !/\S+@\S+\.\S+/.test(workEmail)) {
      newErrors.workEmail = 'Invalid email format';
    }
    
    if (reportingManagerEmail && !/\S+@\S+\.\S+/.test(reportingManagerEmail)) {
      newErrors.reportingManagerEmail = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSectionSave = async (section) => {
    setSectionLoading(true);
    let isValid = false;
    let updateData = {};

    switch (section) {
      case 1:
  isValid = validateSection1();
  if (isValid) {
    // ✅ BETTER: Only send changed fields
    updateData = {
      enableReportingTracking: true,
      preventExtensionStatusUpdate: true
    };
    
    // Compare with original values and only send changed fields
    if (workEmail !== (candidate?.workEmail || '')) updateData.workEmail = workEmail;
    if (reportingManagerName !== (candidate?.reportingManagerName || '')) updateData.reportingManagerName = reportingManagerName;
    if (reportingManagerEmail !== (candidate?.reportingManagerEmail || '')) updateData.reportingManagerEmail = reportingManagerEmail;
    if (reportingManagerPhone !== (candidate?.reportingManagerPhone || '')) updateData.reportingManagerPhone = reportingManagerPhone;
    if (reportingComments !== (candidate?.reportingComments || candidate?.reporting_comments || '')) updateData.reportingComments = reportingComments;
  }
  break;
      default:
        setSectionLoading(false);
        return;
    }

    if (isValid) {
      try {
        await onSave(candidate.id, updateData);
        
        // If it's single mode, close the modal after successful save
        if (mode !== 'both') {
          setTimeout(() => {
            onClose();
          }, 1500);
        }
      } catch (err) {
        console.error('Save error:', err);
      }
    }
    setSectionLoading(false);
  };

  const renderSection1 = () => (
    <div className="space-y-8">
      {/* Section Header with Instructions */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-2xl border border-emerald-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
            <Users2 className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Reporting Structure & Management</h3>
        </div>
        <p className="text-slate-600 text-sm mb-4">Please fill in the information once the candidate has onboarded to the project.</p>
        
        {/* Instructions Box */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <Info className="w-3 h-3 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Instructions</p>
              <p className="text-sm text-blue-800">
                <strong>Fill out this section once the candidate has onboarded the project.</strong>
                <br />
                Please ensure you collect and submit the <strong>candidate's official work email</strong> and the <strong>client-assigned work email</strong> for reference purposes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Work Email - Enhanced with better labeling */}
      <div className="space-y-3">
        <label className="flex items-center text-sm font-semibold text-slate-700">
          <Building className="w-4 h-4 mr-2 text-emerald-600" />
          Official Work Email Address *
        </label>
        <input
          type="email"
          value={workEmail}
          onChange={(e) => setWorkEmail(e.target.value)}
          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200 ${
            errors.workEmail ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
          }`}
          placeholder="candidate@clientcompany.com (Client-assigned work email)"
        />
        <p className="text-xs text-slate-500">
          📧 Enter the candidate's official work email provided by the client company
        </p>
        {errors.workEmail && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.workEmail}</span>
          </div>
        )}
      </div>

      {/* Manager Information Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <User className="w-4 h-4 mr-2 text-emerald-600" />
            Reporting Manager Name *
          </label>
          <input
            type="text"
            value={reportingManagerName}
            onChange={(e) => setReportingManagerName(e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 hover:border-slate-400 transition-all duration-200"
            placeholder="John Smith"
          />
          <p className="text-xs text-slate-500">
            👤 Full name of the candidate's direct reporting manager
          </p>
        </div>

        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Phone className="w-4 h-4 mr-2 text-emerald-600" />
            Manager Phone Number
          </label>
          <input
            type="tel"
            value={reportingManagerPhone}
            onChange={(e) => setReportingManagerPhone(e.target.value)}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 hover:border-slate-400 transition-all duration-200"
            placeholder="+1-555-0123"
          />
          <p className="text-xs text-slate-500">
            📞 Phone number of the reporting manager
          </p>
        </div>

        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Mail className="w-4 h-4 mr-2 text-emerald-600" />
            Manager Email Address *
          </label>
          <input
            type="email"
            value={reportingManagerEmail}
            onChange={(e) => setReportingManagerEmail(e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200 ${
              errors.reportingManagerEmail ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="manager@clientcompany.com"
          />
          <p className="text-xs text-slate-500">
            📧 Email address of the reporting manager for official communications
          </p>
          {errors.reportingManagerEmail && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.reportingManagerEmail}</span>
            </div>
          )}
        </div>
      </div>

      {/* Additional Comments */}
      <div className="space-y-3">
        <label className="flex items-center text-sm font-semibold text-slate-700">
          <FileText className="w-4 h-4 mr-2 text-emerald-600" />
          Additional Notes & Comments
        </label>
        <textarea
          value={reportingComments}
          onChange={(e) => setReportingComments(e.target.value)}
          rows={4}
          className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 hover:border-slate-400 transition-all duration-200"
          placeholder="Any additional notes about reporting structure, special instructions, or important details about the working relationship..."
        />
        <p className="text-xs text-slate-500">
          📝 Optional: Any additional context or special instructions regarding the reporting structure
        </p>
      </div>

      {/* Action Button */}
      <div className="flex justify-end pt-6 border-t border-slate-200">
        <button
          onClick={() => handleSectionSave(1)}
          disabled={sectionLoading}
          className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
        >
          {sectionLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>Save Reporting Information</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  if (!candidate) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden border border-slate-200">
        {/* Enhanced Header */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-8 py-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Users2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  Reporting Information
                </h2>
                <p className="text-emerald-100 text-sm">{candidate.candidateName} • {candidate.client}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-3 hover:bg-white/20 rounded-xl transition-all duration-200 group"
            >
              <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        <div className="p-8 max-h-[calc(95vh-200px)] overflow-y-auto">
          {renderSection1()}
        </div>
      </div>
    </div>
  );
};

export default ReportingInfoModal;